import { useState, useEffect } from 'react'
import { RANKUP_MESSAGES } from '../lib/press'

// ── RankUpBanner ──────────────────────────────────────────────
// Full-width dramatic rank-up moment
// Usage: <RankUpBanner rank={5} onDismiss={() => {}} />

export default function RankUpBanner({ rank, onDismiss }) {
  const [visible, setVisible] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const msg = RANKUP_MESSAGES[rank]

  useEffect(() => {
    // Animate in
    const t1 = setTimeout(() => setVisible(true), 50)
    // Auto-dismiss after 5s
    const t2 = setTimeout(() => handleDismiss(), 5500)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  function handleDismiss() {
    setLeaving(true)
    setTimeout(() => onDismiss?.(), 500)
  }

  if (!msg) return null

  return (
    <>
      <style>{`
        @keyframes rankup-in {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
        @keyframes rankup-out {
          from { transform: translateY(0);     opacity: 1; }
          to   { transform: translateY(-100%); opacity: 0; }
        }
        @keyframes rankup-ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes rankup-stamp {
          0%   { transform: scale(2) rotate(-8deg); opacity: 0; }
          60%  { transform: scale(0.95) rotate(1deg); opacity: 1; }
          80%  { transform: scale(1.04) rotate(-0.5deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes rankup-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }
        .rankup-banner {
          position: fixed;
          top: 0; left: 0; right: 0;
          z-index: 9999;
          animation: ${leaving ? 'rankup-out 0.5s ease forwards' : visible ? 'rankup-in 0.4s cubic-bezier(0.16,1,0.3,1) forwards' : 'none'};
          transform: translateY(-100%);
        }
        .rankup-ticker-inner {
          animation: rankup-ticker 12s linear infinite;
          white-space: nowrap;
        }
        .rankup-stamp {
          animation: rankup-stamp 0.6s cubic-bezier(0.16,1,0.3,1) 0.3s both;
        }
        .rankup-cursor {
          animation: rankup-blink 0.8s step-end infinite;
        }
      `}</style>

      <div className="rankup-banner" onClick={handleDismiss}>
        {/* Ticker tape top */}
        <div style={{
          background: '#C8401A',
          overflow: 'hidden',
          height: 28,
          display: 'flex',
          alignItems: 'center',
        }}>
          <div className="rankup-ticker-inner" style={{
            display: 'flex',
            gap: '3rem',
          }}>
            {Array(16).fill(null).map((_, i) => (
              <span key={i} style={{
                fontFamily: "'Courier New', monospace",
                fontSize: '0.65rem',
                letterSpacing: '0.2em',
                color: 'rgba(255,255,255,0.9)',
                textTransform: 'uppercase',
                userSelect: 'none',
              }}>
                EXTRA ✦ EXTRA ✦ READ ALL ABOUT IT ✦
              </span>
            ))}
          </div>
        </div>

        {/* Main banner */}
        <div style={{
          background: '#0A0908',
          borderBottom: '3px solid #C8401A',
          padding: '1.5rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '2rem',
        }}>
          {/* Left — typewriter headline */}
          <div style={{ flex: 1 }}>
            <p style={{
              fontFamily: "'Courier New', monospace",
              fontSize: '0.65rem',
              color: '#C8401A',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '0.4rem',
            }}>
              RANK UP — THE MARGINS PRESS
            </p>
            <h2 style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(1.4rem, 4vw, 2.2rem)',
              fontWeight: 700,
              color: '#F5F0E8',
              letterSpacing: '-0.02em',
              lineHeight: 1,
              marginBottom: '0.5rem',
            }}>
              You are now a{' '}
              <span style={{ color: '#C8401A' }}>{msg.headline}</span>
              <span className="rankup-cursor" style={{ color: '#C8401A', marginLeft: 2 }}>_</span>
            </h2>
            <p style={{
              fontFamily: "'Courier New', monospace",
              fontSize: '0.75rem',
              color: '#7A7268',
              letterSpacing: '0.04em',
            }}>
              {msg.sub}
            </p>
          </div>

          {/* Centre — stamp */}
          <div className="rankup-stamp" style={{
            flexShrink: 0,
            width: 80,
            height: 80,
            border: '3px solid #C8401A',
            borderRadius: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            transform: 'rotate(-3deg)',
          }}>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: '0.5rem', color: '#C8401A', letterSpacing: '0.15em' }}>THE MARGINS</span>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.6rem', color: '#C8401A', lineHeight: 1 }}>✦</span>
            <span style={{ fontFamily: "'Courier New', monospace", fontSize: '0.48rem', color: '#C8401A', letterSpacing: '0.1em' }}>PRESS PASS</span>
          </div>

          {/* Right — dismiss */}
          <button
            onClick={e => { e.stopPropagation(); handleDismiss() }}
            style={{
              flexShrink: 0,
              background: 'none',
              border: '1px solid #2C2820',
              color: '#7A7268',
              fontFamily: "'Courier New', monospace",
              fontSize: '0.6rem',
              letterSpacing: '0.1em',
              padding: '0.4rem 0.75rem',
              cursor: 'pointer',
            }}
          >
            DISMISS
          </button>
        </div>

        {/* Bottom ticker */}
        <div style={{
          background: '#0F0E0C',
          overflow: 'hidden',
          height: 22,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #1E1C1A',
        }}>
          <div className="rankup-ticker-inner" style={{ display: 'flex', gap: '2rem' }}>
            {Array(20).fill(null).map((_, i) => (
              <span key={i} style={{
                fontFamily: "'Courier New', monospace",
                fontSize: '0.58rem',
                letterSpacing: '0.12em',
                color: '#2C2820',
                textTransform: 'uppercase',
                userSelect: 'none',
              }}>
                {msg.headline} ◆ THE MARGINS ◆ EST. 2026 ◆
              </span>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
